import { PlusOutlined } from '@ant-design/icons';
import { Modal, Upload, type UploadFile, type UploadProps } from 'antd';
import { useState } from 'react';

interface PictureUploadInputProps {
  value?: UploadFile[];
  onChange?: (fileList: UploadFile[]) => void;
  onRemove?: UploadProps['onRemove'];
  maxCount?: number;
  accept?: string;
  disabled?: boolean;
  showRemoveIcon?: boolean;
}

interface PictureObject {
  id: string;
  data: string;
}

const getBase64 = (file: File | Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });

const MAX_DIMENSION = 1500;
const JPEG_QUALITY = 0.7;

const compressImage = async (file: File): Promise<File> => {
  if (!file.type.startsWith('image/')) {
    return file;
  }

  const bitmap = await createImageBitmap(file);
  const scale = Math.min(
    1,
    MAX_DIMENSION / Math.max(bitmap.width, bitmap.height),
  );
  const width = Math.round(bitmap.width * scale);
  const height = Math.round(bitmap.height * scale);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    bitmap.close();
    return file;
  }
  ctx.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY),
  );
  if (!blob || blob.size >= file.size) {
    return file;
  }

  return new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), {
    type: 'image/jpeg',
    lastModified: Date.now(),
  });
};

export const toBase64List = (files: UploadFile[]): Promise<string[]> =>
  Promise.all(
    files.map((f) =>
      f.originFileObj ? getBase64(f.originFileObj) : Promise.resolve(f.url!),
    ),
  );

export const filesToPicturesBase64 = (
  files: UploadFile[],
): Promise<PictureObject[]> =>
  Promise.all(
    files.map((f) =>
      f.originFileObj ? getBase64(f.originFileObj) : Promise.resolve(f.url!),
    ),
  ).then((dataList) =>
    files.map((file, i) => ({ id: file.uid, data: dataList[i] })),
  );

export const base64DataToBlob = (
  dataUrl: string,
): { blob: Blob; extension: string } => {
  const [header, base64] = dataUrl.split(',');
  const mimeType = header.match(/:(.*?);/)?.[1] ?? 'image/jpeg';
  const extension = mimeType === 'image/png' ? 'png' : 'jpeg';
  const bytes = atob(base64);
  const buffer = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) {
    buffer[i] = bytes.charCodeAt(i);
  }
  return { blob: new Blob([buffer], { type: mimeType }), extension };
};

export const picturesBase64ToFiles = (
  pictures: PictureObject[],
): UploadFile[] =>
  pictures.map((picture) => ({
    uid: picture.id,
    name: `photo-${picture.id}.jpg`,
    status: 'done' as const,
    url: picture.data,
  }));

export const PictureUploadInput = ({
  value = [],
  onChange,
  onRemove,
  maxCount = 3,
  accept = 'image/*',
  disabled = false,
  showRemoveIcon = false,
}: PictureUploadInputProps) => {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [previewTitle, setPreviewTitle] = useState('');

  const handlePreview = async (file: UploadFile) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj as File);
    }
    setPreviewImage(file.url || (file.preview as string));
    setPreviewTitle(
      file.name || file.url!.substring(file.url!.lastIndexOf('/') + 1),
    );
    setPreviewOpen(true);
  };

  const handleChange: UploadProps['onChange'] = async ({ fileList }) => {
    const existingUids = new Set(value.map((file) => file.uid));

    const processedFileList = await Promise.all(
      fileList.map(async (file) => {
        if (existingUids.has(file.uid) || !file.originFileObj) {
          return file;
        }
        const compressedFile = await compressImage(file.originFileObj as File);
        return {
          ...file,
          originFileObj: compressedFile as UploadFile['originFileObj'],
        };
      }),
    );

    onChange?.(processedFileList);
  };

  return (
    <>
      <Upload
        listType="picture-card"
        fileList={value}
        onPreview={handlePreview}
        onChange={handleChange}
        onRemove={onRemove}
        beforeUpload={() => false}
        accept={accept}
        multiple={false}
        maxCount={maxCount}
        capture="environment"
        disabled={disabled}
        showUploadList={{ showRemoveIcon }}
      >
        {value.length >= maxCount ? null : (
          <div>
            <PlusOutlined />
            <div style={{ marginTop: 8 }}>Ajouter</div>
          </div>
        )}
      </Upload>

      <Modal
        open={previewOpen}
        title={previewTitle}
        footer={null}
        onCancel={() => setPreviewOpen(false)}
      >
        <img alt="preview" style={{ width: '100%' }} src={previewImage} />
      </Modal>
    </>
  );
};
